'use client';

/**
 * "Is something full screen over the page?" for every window on the canvas
 * (lane B, 2026-09-29; ads build plan section 2.4 "one window per device").
 *
 * WHY EVERY WINDOW ASKS. Desktop full screen presents ONE app window with the
 * browser's element full screen (maximize-app-node.ts): that window's app bar
 * carries the ad (placement `fullScreen`), and every other window's title bar
 * is still in the document behind the full-screen element. The title-bar
 * slot's viewability check is an IntersectionObserver, which knows nothing
 * of the top layer, so a title-bar ad under a full-screen app would count as
 * seen and the device would carry two ads. The full-screen window's own
 * `maximizeOverrideActive` flag lives in its content node and no other window
 * can read it; the document can answer for all of them.
 *
 * The root going full screen (the whole page) covers nothing, so it does not
 * count. An app's own iframe going full screen covers the canvas like a
 * window does, so it does.
 */

import { useEffect, useState } from 'react';
import { currentFullscreenElement } from '@/components/workspace/nodes/maximize-app-node';

/** True while an element other than the root is the browser's full-screen element. */
export function documentCoveredByFullscreen(doc: Document = document): boolean {
  const el = currentFullscreenElement(doc);
  return !!el && el !== doc.documentElement;
}

/** `documentCoveredByFullscreen`, kept current across `fullscreenchange` (Safari's prefixed event too). */
export function useDocumentFullscreen(): boolean {
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    const read = () => setCovered(documentCoveredByFullscreen());
    read();
    document.addEventListener('fullscreenchange', read);
    document.addEventListener('webkitfullscreenchange', read);
    return () => {
      document.removeEventListener('fullscreenchange', read);
      document.removeEventListener('webkitfullscreenchange', read);
    };
  }, []);
  return covered;
}
